/**
 * NumberField Component - Number input with formatting
 */

import React, { useCallback, useState, useEffect } from 'react';
import { NumberFieldProps } from '../../../types/forms';
import { BaseField } from './BaseField';

export const NumberField: React.FC<NumberFieldProps> = ({
  name,
  label,
  placeholder,
  helpText,
  required = false,
  disabled = false,
  readonly = false,
  error,
  className = '',
  value,
  min,
  max,
  step = 1,
  precision,
  thousandSeparator = false,
  prefix,
  suffix,
  onChange,
  onBlur,
  onFocus,
}) => {
  const fieldId = `field-${name}`;
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Format number for display
  const formatNumber = useCallback(
    (num: number | null): string => {
      if (num === null || num === undefined || isNaN(num)) return '';

      let formatted = num.toString();

      if (precision !== undefined) {
        formatted = num.toFixed(precision);
      }

      if (thousandSeparator && !isFocused) {
        const parts = formatted.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        formatted = parts.join('.');
      }

      if (prefix && !isFocused) formatted = prefix + formatted;
      if (suffix && !isFocused) formatted = formatted + suffix;

      return formatted;
    },
    [precision, thousandSeparator, prefix, suffix, isFocused]
  );

  // Parse display value to number
  const parseNumber = useCallback(
    (str: string): number | null => {
      if (!str) return null;

      // Remove prefix, suffix, and thousand separators
      let cleaned = str;
      if (prefix) cleaned = cleaned.replace(prefix, '');
      if (suffix) cleaned = cleaned.replace(suffix, '');
      if (thousandSeparator) cleaned = cleaned.replace(/,/g, '');

      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? null : parsed;
    },
    [prefix, suffix, thousandSeparator]
  );

  // Update display value when value prop changes
  useEffect(() => {
    setDisplayValue(formatNumber(value));
  }, [value, formatNumber]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      setDisplayValue(inputValue);

      const parsed = parseNumber(inputValue);
      if (parsed !== null) {
        // Check min/max constraints
        if (min !== undefined && parsed < min) return;
        if (max !== undefined && parsed > max) return;
      }

      onChange?.(parsed);
    },
    [parseNumber, min, max, onChange]
  );

  const handleFocus = useCallback(
    (_e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      // Show raw number when focused
      if (value !== null && value !== undefined) {
        setDisplayValue(value.toString());
      }
      onFocus?.();
    },
    [value, onFocus]
  );

  const handleBlur = useCallback(
    (_e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      // Format number when blurred
      setDisplayValue(formatNumber(value));
      onBlur?.();
    },
    [value, formatNumber, onBlur]
  );

  const inputClasses = `
    w-full px-3 py-2
    border rounded-md shadow-sm
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
    ${error ? 'border-red-300' : 'border-gray-300'}
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
    ${readonly ? 'bg-gray-50' : ''}
    transition-colors duration-200
  `;

  return (
    <BaseField
      name={name}
      label={label}
      helpText={helpText}
      required={required}
      disabled={disabled}
      readonly={readonly}
      error={error}
      className={className}
      fieldId={fieldId}
    >
      <input
        id={fieldId}
        name={name}
        type="text"
        inputMode="decimal"
        value={displayValue}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        readOnly={readonly}
        required={required}
        className={inputClasses}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        aria-invalid={!!error}
        aria-describedby={error ? `${fieldId}-error` : undefined}
      />
    </BaseField>
  );
};

// Currency field variant
export const CurrencyField: React.FC<Omit<NumberFieldProps, 'prefix' | 'precision' | 'thousandSeparator'> & { currency?: string }> = ({
  currency = 'USD',
  ...props
}) => {
  const currencySymbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    CHF: 'CHF ',
    JPY: '¥',
  };

  return (
    <NumberField
      {...props}
      prefix={currencySymbols[currency] || currency + ' '}
      precision={2}
      thousandSeparator={true}
    />
  );
};

// Percentage field variant
export const PercentageField: React.FC<Omit<NumberFieldProps, 'suffix'>> = (props) => (
  <NumberField {...props} suffix="%" min={0} max={100} />
);

export default NumberField;